import axios from 'axios';
import { logger } from '../utils/logger';

// ⚠️ IMPORTANT: DO NOT CHANGE THIS TO HARDCODED URL IN DEVELOPMENT!
// This MUST use '/api' in development to work with the proxy and avoid CORS errors.
// The proxy (setupProxy.js) forwards /api requests to https://securereservation.org/hotels/api
// Use proxy in development (localhost), full URL in production
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'https://securereservation.org/hotels/api'  // Proxy will forward to https://securereservation.org/hotels/api
  : 'https://securereservation.org/hotels/api';

/**
 * Fetch room availability from the API
 * 
 * ⚠️ DYNAMIC CONTROL: All parameters are controlled from components (mobile/desktop)
 * No static defaults - values come from URL parameters or component state
 * 
 * @param {Object} params - Query parameters (all controlled from components)
 * @param {string} params.hotel_id - Hotel accommodation ID (REQUIRED - from URL or component state)
 * @param {string} params.checkin - Check-in date (DD-MM-YYYY) (REQUIRED)
 * @param {string} params.checkout - Check-out date (DD-MM-YYYY) (REQUIRED)
 * @param {number} params.adult - Number of adults (REQUIRED - min: 1)
 * @param {number} params.child - Number of children (optional, defaults to 0)
 * @param {string} params.childAges - Child ages as comma-separated string (optional, e.g., "5,8")
 * @param {string} params.promo - Promotion code (optional - only if provided)
 * @param {string} params.profileid - Profile ID (optional - only if provided)
 * @param {number} params.multilanguageid - Multi-language ID (optional, defaults to 1)
 * @param {string} params.currency - Currency code (optional - only if provided)
 * @param {string} params.device - Device type ('mobile' or 'desktop' - from component)
 * @param {string} params.ratecode - Rate code (optional - only if provided in URL)
 * @returns {Promise<Array>} Array of room availability data
 */
export const fetchRoomAvailability = async (params = {}) => {
  try {
    // Extract parameters - NO static defaults, all controlled from components
    // Support both hotel_id and accommodationid for backward compatibility
    const hotel_id = params.hotel_id || params.accommodationid;
    const {
      checkin,
      checkout,
      adult,
      child,
      childAges,
      promo,
      profileid,
      multilanguageid,
      currency,
      device,
      ratecode
    } = params;

    // Validate required parameters
    if (!checkin || !checkout) {
      throw new Error('Check-in and check-out dates are required');
    }
    
    if (!hotel_id) {
      throw new Error('Hotel ID is required');
    }
    
    if (!adult || adult < 1) {
      throw new Error('At least 1 adult is required');
    }

    // Build query parameters - only include non-empty values
    const queryParams = new URLSearchParams({
      accommodationid: hotel_id.toString(),
      checkin: checkin,
      checkout: checkout,
      adult: adult.toString(),
      child: (child || 0).toString(),
      multilanguageid: (multilanguageid || 1).toString(),
    });
    
    // Add optional parameters only if they have values
    if (childAges) {
      queryParams.append('childAges', childAges);
    }
    if (promo) {
      queryParams.append('promo', promo);
    }
    if (profileid) {
      queryParams.append('profileid', profileid);
    }
    if (currency) {
      queryParams.append('currency', currency);
    }
    if (device) {
      queryParams.append('device', device);
    }
    if (ratecode) {
      queryParams.append('ratecode', ratecode);
    }

    // IMPORTANT: Use API_BASE_URL (not hardcoded URL) so proxy works in development
    const url = `${API_BASE_URL}/getavailability?${queryParams.toString()}`;
    
    let response;
    const maxRetries = 2;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        response = await axios.get(url, {
          responseType: 'text',
          timeout: 30000,
          headers: {
            'Accept': 'application/json, text/plain, */*',
          },
          withCredentials: false,
        });
        break; // Success, exit retry loop
      } catch (axiosError) {
        const isNetworkError = axiosError.code === 'ERR_NETWORK' ||
          (axiosError.message && axiosError.message.includes('Network Error') && !axiosError.response);
        const isCorsError = axiosError.message && axiosError.message.includes('CORS');

        // Retry on network errors (not on CORS or other errors)
        if (isNetworkError && attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1500 * (attempt + 1)));
          continue;
        }

        if (isCorsError) {
          if (process.env.NODE_ENV === 'development') {
            throw new Error('CORS Error: Make sure the development server is running and the proxy is configured. Please restart the development server.');
          } else {
            throw new Error('CORS Error: The API server may not allow requests from this origin. Please contact the API administrator.');
          }
        }

        if (isNetworkError) {
          if (process.env.NODE_ENV === 'development') {
            throw new Error('Network Error: Make sure the development server is running and the proxy is configured. Please restart the development server. If the issue persists, check the terminal for proxy logs.');
          } else {
            throw new Error('Network Error: Could not connect to the API server. Please check your internet connection.');
          }
        }

        throw axiosError;
      }
    }
    
    // Check if response is empty
    if (!response.data || (typeof response.data === 'string' && response.data.trim() === '')) {
      return [];
    }
    
    // The API returns an array directly, but may have PHP warnings before the JSON
    // We need to extract the JSON part if there are warnings
    let data = response.data;
    
    // If response is a string, try to parse it
    if (typeof data === 'string') {
      // Remove PHP warnings and find the JSON array
      // Look for the first occurrence of '[' that's likely the start of JSON
      const jsonStart = data.indexOf('[');
      const jsonEnd = data.lastIndexOf(']');
      
      if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
        try {
          const jsonString = data.substring(jsonStart, jsonEnd + 1);
          data = JSON.parse(jsonString);
        } catch (parseError) {
          // Try to parse the entire response as JSON
          try {
            data = JSON.parse(data);
          } catch (e) {
            throw new Error('Invalid API response format - could not parse JSON. The API may be returning an error.');
          }
        }
      } else {
        // Try to parse the entire response as JSON
        try {
          data = JSON.parse(data);
        } catch (e) {
          // If it's not JSON, it might be an error message
          if (data.includes('error') || data.includes('Error') || data.includes('Warning')) {
            throw new Error('API returned an error. Please check the console for details.');
          }
          throw new Error('Invalid API response format - no valid JSON found');
        }
      }
    }
    
    // If it's already an array, return it
    if (Array.isArray(data)) {
      return data;
    }

    // If it's an object with a data property, return that
    if (data && Array.isArray(data.data)) {
      return data.data;
    }

    // Handle new API response structure: {Result: {AccommodationId, HotelRooms: {...}}}
    if (data && data.Result && data.Result.HotelRooms) {
      const hotelRooms = data.Result.HotelRooms;
      // HotelRooms can be a single object or an array
      if (Array.isArray(hotelRooms)) {
        return hotelRooms;
      }
      // If it's a single room object, wrap it in an array
      return [hotelRooms];
    }

    // If data is an object, try to extract array from it
    if (data && typeof data === 'object') {
      const arrayKeys = Object.keys(data).filter(key => Array.isArray(data[key]));
      if (arrayKeys.length > 0) {
        return data[arrayKeys[0]];
      }
    }
    return [];
  } catch (error) {
    // Provide more detailed error messages
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      throw new Error(`API Error: Server returned status ${error.response.status}`);
    } else if (error.request) {
      // The request was made but no response was received
      throw new Error('Network Error: No response from server. Please check your internet connection.');
    } else if (error.code === 'ECONNABORTED') {
      // Timeout error
      throw new Error('Request timeout: The server took too long to respond.');
    } else {
      // Something happened in setting up the request that triggered an Error
      throw new Error(error.message || 'Failed to fetch room availability');
    }
  }
};

/**
 * Transform API response - Handles actual API structure with capitalized field names
 * @param {Array} apiRooms - Array of rooms from API
 * @returns {Array} Transformed room data with all API fields preserved
 */
export const transformApiRoomsToLocalFormat = (apiRooms) => {
  if (!Array.isArray(apiRooms) || apiRooms.length === 0) {
    return [];
  }

  const roomsMap = new Map();

  apiRooms.forEach((apiRoom) => {
    // Extract RoomId (capitalized)
    const roomId = apiRoom.RoomId || apiRoom.roomId || apiRoom.roomid;
    
    if (!roomsMap.has(roomId)) {
      // Extract RoomImages - handle nested structure
      let roomImages = [];
      if (apiRoom.RoomImages) {
        if (apiRoom.RoomImages.RoomImage) {
          // Handle single image object
          if (apiRoom.RoomImages.RoomImage['@attributes']) {
            roomImages.push(apiRoom.RoomImages.RoomImage['@attributes'].Photo_Max500);
          } else if (Array.isArray(apiRoom.RoomImages.RoomImage)) {
            // Handle array of images
            roomImages = apiRoom.RoomImages.RoomImage.map(img => 
              img['@attributes'] ? img['@attributes'].Photo_Max500 : null
            ).filter(Boolean);
          }
        } else if (Array.isArray(apiRoom.RoomImages)) {
          roomImages = apiRoom.RoomImages;
        }
      }
      
      // Extract RatePlanDetails
      const ratePlanDetails = apiRoom.RatePlanDetails || {};
      let ratePlansArray = ratePlanDetails.RatePlans;
      
      // Handle RatePlans - can be array, single object, or undefined
      if (!ratePlansArray) {
        ratePlansArray = [];
      } else if (!Array.isArray(ratePlansArray)) {
        // If it's a single object, convert to array
        ratePlansArray = [ratePlansArray];
      }
      
      // Get first rate plan for room defaults (if available)
      const firstRatePlan = ratePlansArray.length > 0 ? ratePlansArray[0] : {};
      
      // Create room entry - preserve ALL API fields
      const room = {
        // Preserve all original API fields
        ...apiRoom,
        
        // Compatibility fields for existing component
        id: parseInt(roomId || 0),
        name: apiRoom.RoomName || apiRoom.roomName || '', // koi fake "Room" naam nahi
        image: roomImages.length > 0 ? roomImages[0] : null,
        images: roomImages,
        basePrice: parseFloat(firstRatePlan.Rate || firstRatePlan.BaseRate || 0),
        originalPrice: parseFloat(firstRatePlan.BaseRate || firstRatePlan.Rate || 0),
        size: (() => {
          // Only build the size string when RoomSize is a valid positive number —
          // otherwise parseFloat(non-numeric) = NaN → "NaN sq. ft." (bug). Missing → 'N/A' (UI hides).
          const s = parseFloat(apiRoom.RoomSize);
          return (!isNaN(s) && s > 0) ? `${s} sq. ft.` : 'N/A';
        })(),
        guests: parseInt(apiRoom.RoomMaxPerson || firstRatePlan.MaxPerson) || null, // missing → null (UI hide karega), fake 2 nahi
        amenities: extractAmenities(apiRoom),
        facilityGroups: extractFacilityGroups(apiRoom),
        features: extractFeatures(apiRoom),
        description: apiRoom.RoomDescription || apiRoom.roomDescription || '',
        rating: null, // API rating nahi deta — koi fake rating NAHI (badge tabhi dikhe jab real rating ho)
        discount: null, // Will be calculated from rate plan
        taxes: firstRatePlan.Taxs || null,
        totalroom: parseInt(firstRatePlan.NoOfRoomsAvailable || 0),
        BedSize: apiRoom.BedSize || apiRoom.bedSize || '',
        ratePlans: []
      };
      
      roomsMap.set(roomId, room);
    }
    
    // Extract RatePlanDetails for rate plan
    const ratePlanDetails = apiRoom.RatePlanDetails || {};
    let ratePlansArray = ratePlanDetails.RatePlans;
    
    // Handle RatePlans - can be array, single object, or undefined
    if (!ratePlansArray) {
      ratePlansArray = [];
    } else if (!Array.isArray(ratePlansArray)) {
      // If it's a single object, convert to array
      ratePlansArray = [ratePlansArray];
    }
    
    // Process each rate plan in the array
    ratePlansArray.forEach((ratePlansData) => {
      // Extract RateDetailsByDate
      const rateDetailsByDate = ratePlansData.RateDetailsByDate || {};
      let ratePerDate = rateDetailsByDate.RatePerDate;
      
      // Handle RatePerDate - can be array, single object, or undefined
      if (!ratePerDate) {
        ratePerDate = [];
      } else if (!Array.isArray(ratePerDate)) {
        // If it's a single object, convert to array
        ratePerDate = [ratePerDate];
      }
      
      // Extract Taxs
      const taxs = ratePlansData.Taxs || {};
      let taxArray = taxs.Tax;
      
      // Handle Tax - can be array, single object, or undefined
      if (!taxArray) {
        taxArray = [];
      } else if (!Array.isArray(taxArray)) {
        // If it's a single object, convert to array
        taxArray = [taxArray];
      }
      
      // Debug: Log registeredUser and secretDeal status
      const registeredUser = ratePlansData.RegisteredUser;
      const isSecretDeal = registeredUser === 1 || registeredUser === '1' || String(registeredUser) === '1';
      logger.debug(`Rate Plan: ${ratePlansData.RatePlanName || 'Unknown'} - RegisteredUser: ${registeredUser} (${typeof registeredUser}) - SecretDeal: ${isSecretDeal}`);
      // Create rate plan - preserve ALL API fields
      const ratePlan = {
        // Preserve all original API fields
        ...apiRoom,
        
        // RatePlan specific fields
        RatePlanDetails: ratePlanDetails,
        
        // Compatibility fields for existing component
        id: `${roomId}_${ratePlansData.RatePlanId || '1'}`,
        ratePlanId: ratePlansData.RatePlanId,
        price: parseFloat(ratePlansData.Rate || 0),
        basePrice: parseFloat(ratePlansData.Rate || ratePlansData.Rate || 0),
        name: ratePlansData.RatePlanName || '',
        description: (() => {
          // Handle BreakFastDescription or Included - can be string, array, null, or undefined
          const desc = ratePlansData.BreakFastDescription || ratePlansData.Included;
          if (!desc) return '';
          if (Array.isArray(desc)) {
            return desc.filter(item => typeof item === 'string').join(', ');
          }
          return typeof desc === 'string' ? desc : '';
        })(),
        priceModifier: 0,
        cancellation: (() => {
          // Handle CancellationPolicy - can be string, array, null, or undefined.
          // NO fake default: agar API policy nahi deti to '' rakho (UI use hide karega) —
          // kabhi "Free cancellation" fabricate NAHI karna (jhooti/legally-galat info).
          const policy = ratePlansData.CancellationPolicy;
          if (!policy) return '';
          if (Array.isArray(policy)) {
            return policy.filter(item => typeof item === 'string').join(', ') || '';
          }
          return typeof policy === 'string' ? policy : '';
        })(),
        cancellationPolicy: Array.isArray(ratePlansData.CancellationPolicy)
          ? ratePlansData.CancellationPolicy.join(', ')
          : (ratePlansData.CancellationPolicy || ''),
        cancellationPolicyType: Array.isArray(ratePlansData.CancellationPolicyType)
          ? ratePlansData.CancellationPolicyType.join(', ')
          : (ratePlansData.CancellationPolicyType || ''),
        breakfast: (() => {
          // Handle BreakFastTypeName - can be string, array, null, or undefined
          // "Not Included" should return false, only true "Included" values count
          const breakFastType = ratePlansData.BreakFastTypeName;
          if (!breakFastType) return false;
          const isIncluded = (val) => {
            if (typeof val !== 'string') return false;
            const lower = val.toLowerCase().trim();
            if (lower.includes('not included')) return false;
            return lower.includes('included') || lower === 'breakfast';
          };
          if (Array.isArray(breakFastType)) {
            return breakFastType.some(item => isIncluded(item));
          }
          return isIncluded(breakFastType);
        })(),
        breakFastTypeName: Array.isArray(ratePlansData.BreakFastTypeName) 
          ? ratePlansData.BreakFastTypeName.join(', ') 
          : (ratePlansData.BreakFastTypeName || ''),
        breakFastDescription: Array.isArray(ratePlansData.BreakFastDescription)
          ? ratePlansData.BreakFastDescription.join(', ')
          : (ratePlansData.BreakFastDescription || ''),
        secretDeal: (() => {
          // If registeredUser === 1, then secretDeal is active
          const registeredUser = ratePlansData.RegisteredUser;
          return registeredUser === 1 || registeredUser === '1' || String(registeredUser) === '1';
        })(),
        memberDiscount: 0,
        maxGuests: parseInt(ratePlansData.MaxPerson || apiRoom.RoomMaxPerson) || null, // missing → null (UI hide karega), fake 2 nahi
        maxChild: parseInt(ratePlansData.MaxChild || 0),
        roomsAvailable: parseInt(ratePlansData.NoOfRoomsAvailable || 0),
        badge: (() => {
          // Handle PromotionName - can be string, array, null, or undefined
          const promo = ratePlansData.PromotionName;
          if (!promo) return '';
          if (Array.isArray(promo) && promo.length > 0) {
            return '';
          }
          if (typeof promo === 'string' && promo.trim()) {
            return '';
          }
          return '';
        })(),
        badgeColor: (() => {
          const promo = ratePlansData.PromotionName;
          if (!promo) return 'blue';
          if (Array.isArray(promo) && promo.length > 0) {
            return 'green';
          }
          if (typeof promo === 'string' && promo.trim()) {
            return 'green';
          }
          return 'blue';
        })(),
        promotionName: Array.isArray(ratePlansData.PromotionName)
          ? ratePlansData.PromotionName.join(', ')
          : (ratePlansData.PromotionName || ''),
        promotionTypeName: Array.isArray(ratePlansData.PromotionTypeName)
          ? ratePlansData.PromotionTypeName.join(', ')
          : (ratePlansData.PromotionTypeName || ''),
        promotionCoupen: Array.isArray(ratePlansData.PromotionCoupen)
          ? ratePlansData.PromotionCoupen.join(', ')
          : (ratePlansData.PromotionCoupen || ''),
        currency: ratePlansData.CurrencyCode || '',
        CurrencyCode: ratePlansData.CurrencyCode || '', // Preserve original CurrencyCode field
        ratePlanCurrencyCode: (typeof ratePlansData.RatePlanCurrencyCode === 'string' && ratePlansData.RatePlanCurrencyCode.trim()) ? ratePlansData.RatePlanCurrencyCode.trim() : '',
        conversionRate: ratePlansData.ConversionRate,
        convertedCurrency: (typeof ratePlansData.ConvertedCurrency === 'string' && ratePlansData.ConvertedCurrency.trim()) ? ratePlansData.ConvertedCurrency.trim() : '',
        convertedRate: ratePlansData.ConvertedRate,
        advancePayment: parseFloat(ratePlansData.AdvancePayment || 0),
        registeredUser: ratePlansData.RegisteredUser,
        noOfBedRooms: parseInt(ratePlansData.NoOfBedRooms) || null, // missing → null, fake 1 nahi
        refundStatus: ratePlansData.RefundStatus,
        bookingPolicy: ratePlansData.BookingPolicy,
        noShowPolicyType: ratePlansData.NoShowPolicyType,
        noShowPolicy: ratePlansData.NoShowPolicy,
        included: ratePlansData.Included,
        highlight: ratePlansData.Highlight,
        
        // Rate details by date
        rateDetailsByDate: Array.isArray(ratePerDate) ? ratePerDate.map(rate => {
          // Handle both object with @attributes and direct object
          const attrs = rate['@attributes'] || rate;
          return {
            date: attrs.Date || attrs.date,
            rate: parseFloat(attrs.Rate || attrs.rate || 0),
            baseRate: parseFloat(attrs.BaseRate || attrs.baseRate || attrs.Rate || attrs.rate || 0),
            convertedRate: attrs.ConvertedRate || attrs.convertedRate || '',
            discountValue: attrs.DiscountValue || attrs.discountValue || '',
            discountType: attrs.DiscountType || attrs.discountType || '0'
          };
        }) : [],
        
        // Occupancy rate
        occupancyRate: ratePlansData.OccupancyRate || {},
        occupancyRateDay: ratePlansData.OccupancyRateDay || {},
        
        // Discount rate per date
        discountRatePerDate: ratePlansData.DiscountRatePerDate || {},
        promotionDiscount: (() => {
          const discountData = ratePlansData.DiscountRatePerDate || {};
          if (discountData.Discount && parseFloat(discountData.Discount) > 0) {
            return {
              promotionName: discountData.PromotionName || '',
              promotionTypeName: discountData.PromotionTypeName || '',
              discount: parseFloat(discountData.Discount || 0),
              afterDiscountAmount: parseFloat(discountData.AfterDiscountAmount || 0),
              discountValue: parseFloat(discountData.DiscountValue || 0),
              discountType: discountData.DiscountType || 'Percentage',
              originalPrice: parseFloat(ratePlansData.Rate || 0)
            };
          }
          return null;
        })(),
        
        // Taxes
        taxes: Array.isArray(taxArray) ? taxArray.map(tax => {
          // Handle both object with @attributes and direct object
          const attrs = tax['@attributes'] || tax;
          return {
            taxSorting: parseInt(attrs.TaxSorting || attrs.taxSorting || 0),
            taxId: parseInt(attrs.TaxId || attrs.taxId || 0),
            taxTypeId: parseInt(attrs.TaxTypeId || attrs.taxTypeId || 0),
            taxName: attrs.TaxName || attrs.taxName || '',
            taxType: attrs.TaxType || attrs.taxType || '',
            taxPriceModeId: parseInt(attrs.TaxPriceModeId || attrs.taxPriceModeId || 0),
            taxPriceMode: attrs.TaxPriceMode || attrs.taxPriceMode || '',
            taxValue: parseFloat(attrs.TaxValue || attrs.taxValue || 0),
            taxChargeType: attrs.TaxChargeType || attrs.taxChargeType || '',
            taxChargeTypeId: parseInt(attrs.TaxChargeTypeId || attrs.taxChargeTypeId || 0)
          };
        }) : []
      };
      
      const room = roomsMap.get(roomId);
      room.ratePlans.push(ratePlan);
    });
  });

  return Array.from(roomsMap.values());
};

/**
 * Extract ALL amenities from API room data (no limit, no hardcoded fallback)
 */
const extractAmenities = (apiRoom) => {
  const facilityName = apiRoom.RoomFacilityName || apiRoom.roomFacilityName;
  const facilityNameStr = typeof facilityName === 'string' ? facilityName : '';

  if (facilityNameStr) {
    const facilities = facilityNameStr.split(',').map(f => f.trim()).filter(Boolean);
    return [...new Set(facilities)];
  }

  return [];
};

/**
 * Extract facilities grouped by RoomFacilityGroupName
 * Returns: { "Featured": ["Air conditioning", ...], "Room Amenities": [...], ... }
 */
const extractFacilityGroups = (apiRoom) => {
  const facilityName = apiRoom.RoomFacilityName || apiRoom.roomFacilityName;
  const facilityGroupName = apiRoom.RoomFacilityGroupName || apiRoom.roomFacilityGroupName;

  const facilityNameStr = typeof facilityName === 'string' ? facilityName : '';
  const facilityGroupNameStr = typeof facilityGroupName === 'string' ? facilityGroupName : '';

  if (!facilityNameStr || !facilityGroupNameStr) return {};

  const names = facilityNameStr.split(',').map(f => f.trim());
  const groups = facilityGroupNameStr.split(',').map(g => g.trim());

  const grouped = {};
  const len = Math.min(names.length, groups.length);

  for (let i = 0; i < len; i++) {
    if (!names[i] || !groups[i]) continue;
    const groupKey = groups[i];
    if (!grouped[groupKey]) grouped[groupKey] = [];
    if (!grouped[groupKey].includes(names[i])) {
      grouped[groupKey].push(names[i]);
    }
  }

  return grouped;
};

/**
 * Extract features from API room data
 */
const extractFeatures = (apiRoom) => {
  const features = [];

  const bedSize = apiRoom.BedSize || apiRoom.bedSize;
  if (bedSize) {
    features.push(bedSize);
  }

  const facilityName = apiRoom.RoomFacilityName || apiRoom.roomFacilityName;
  const facilityNameStr = typeof facilityName === 'string' ? facilityName : '';

  if (facilityNameStr) {
    const facilities = facilityNameStr.split(',').map(f => f.trim());
    const viewFeatures = facilities.filter(f =>
      f.toLowerCase().includes('view') ||
      f.toLowerCase().includes('beach') ||
      f.toLowerCase().includes('sea') ||
      f.toLowerCase().includes('outdoor') ||
      f.toLowerCase().includes('sitout')
    );
    features.push(...viewFeatures.slice(0, 2));
  }

  return features;
};

